/**
 * <sm-audio-player> — the SALTYmf branded episode player.
 *
 * The element is rendered complete by snippets/sm-audio-player.liquid; this
 * script only wires behaviour to it, so the markup stays themeable and every
 * label stays editable in the theme editor.
 *
 * The audio file and the per-episode platform links come from one of two
 * places, in order:
 *   1. the article's podcast.* metafields, rendered straight into data-*
 *   2. assets/sm-podcast-episodes.json, looked up by publish timestamp
 *   3. the shows' RSS feeds, for episodes published since that file was
 *      last built (Buzzsprout serves them with open CORS). Read at most once
 *      per visit and kept in sessionStorage.
 * Anything still missing falls back to the show-level links the section
 * settings carry, which is why the links work before this script runs.
 */

const INDEX_CACHE = new Map();

function loadIndex(url) {
  if (!INDEX_CACHE.has(url)) {
    INDEX_CACHE.set(
      url,
      fetch(url)
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null)
    );
  }
  return INDEX_CACHE.get(url);
}

const FEED_CACHE_KEY = 'smPodFeed';
const FEED_CACHE_MS = 6 * 60 * 60 * 1000;
let FEED_PROMISE = null;

/** "3064" or "51:04" or "1:02:11" -> seconds, as the index builder reads it */
function toSeconds(raw) {
  if (!raw) return 0;
  if (!raw.includes(':')) return parseInt(raw, 10) || 0;
  return raw.split(':').map(Number).reduce((acc, part) => acc * 60 + part, 0);
}

/** One feed as { publishTimestamp: episode }, in the index's own shape. */
async function readFeed(rss, showKey) {
  const out = {};
  try {
    // Revalidate rather than trust the browser's HTTP cache: a release day
    // copy fetched minutes before the episode went out would not have it.
    const res = await fetch(rss, { cache: 'no-cache' });
    if (!res.ok) return out;
    const xml = new DOMParser().parseFromString(await res.text(), 'text/xml');
    xml.querySelectorAll('item').forEach((item) => {
      const first = (name) => item.getElementsByTagName(name)[0];
      const text = (name) => (first(name) ? first(name).textContent.trim() : '');
      const attr = (name, key) => (first(name) ? first(name).getAttribute(key) || '' : '');
      const time = Date.parse(text('pubDate'));
      const audio = attr('enclosure', 'url');
      if (Number.isNaN(time) || !audio) return;
      const chapters = Array.from(item.getElementsByTagName('psc:chapter')).map((c) => ({
        start: toSeconds(c.getAttribute('start') || ''),
        title: c.getAttribute('title') || '',
      }));
      out[String(Math.floor(time / 1000))] = {
        show: showKey,
        title: text('title'),
        audio,
        seconds: toSeconds(text('itunes:duration')),
        artwork: attr('itunes:image', 'href'),
        transcript: attr('podcast:transcript', 'url'),
        chapters: chapters.length ? chapters : null,
      };
    });
  } catch (e) {
    // An unreadable feed leaves the episode on the "not available" note.
  }
  return out;
}

/**
 * Every show's feed merged, cached for the visit. Pass the episode the page
 * needs: a cached copy that lacks it was saved before the episode went out,
 * so it is read again instead of leaving a new release unplayable for hours.
 */
function loadFeeds(index, wanted) {
  if (!FEED_PROMISE) {
    FEED_PROMISE = (async () => {
      try {
        const cached = JSON.parse(sessionStorage.getItem(FEED_CACHE_KEY) || 'null');
        const fresh = cached && Date.now() - cached.at < FEED_CACHE_MS;
        if (fresh && (!wanted || cached.episodes[wanted])) return cached.episodes;
      } catch (e) {
        // Fall through and read the feeds.
      }
      const shows = Object.entries(index.shows || {}).filter(([, show]) => show.rss);
      const maps = await Promise.all(shows.map(([key, show]) => readFeed(show.rss, key)));
      const episodes = Object.assign({}, ...maps);
      try {
        sessionStorage.setItem(FEED_CACHE_KEY, JSON.stringify({ at: Date.now(), episodes }));
      } catch (e) {
        // Storage can be full or blocked; this page still plays.
      }
      return episodes;
    })();
  }
  return FEED_PROMISE;
}

/** 3064 -> "51:04", 4200 -> "1:10:00" */
function clock(total) {
  if (!Number.isFinite(total) || total < 0) total = 0;
  const s = Math.floor(total % 60);
  const m = Math.floor((total / 60) % 60);
  const h = Math.floor(total / 3600);
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

class SmAudioPlayer extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';

    this.audio = new Audio();
    this.audio.preload = 'none';
    this.scrubbing = false;

    this.els = {
      play: this.querySelector('[data-ap-play]'),
      back: this.querySelector('[data-ap-back]'),
      forward: this.querySelector('[data-ap-forward]'),
      speed: this.querySelector('[data-ap-speed]'),
      mute: this.querySelector('[data-ap-mute]'),
      range: this.querySelector('[data-ap-range]'),
      buffer: this.querySelector('[data-ap-buffer]'),
      fill: this.querySelector('[data-ap-fill]'),
      current: this.querySelector('[data-ap-current]'),
      duration: this.querySelector('[data-ap-duration]'),
      art: this.querySelector('[data-ap-art]'),
      chapters: this.querySelector('[data-ap-chapters]'),
      chapterList: this.querySelector('[data-ap-chapter-list]'),
      status: this.querySelector('[data-ap-status]'),
    };

    this.speeds = (this.dataset.speeds || '1,1.25,1.5,1.75,2')
      .split(',')
      .map((n) => parseFloat(n))
      .filter((n) => n > 0);
    this.speedIndex = 0;
    this.skipBack = parseInt(this.dataset.skipBack, 10) || 15;
    this.skipForward = parseInt(this.dataset.skipForward, 10) || 30;

    this.bindControls();
    this.bindAudio();
    this.resolveEpisode();
  }

  disconnectedCallback() {
    if (this.audio) this.audio.pause();
  }

  /* ------------------------------------------------------------ resolving */

  async resolveEpisode() {
    // Metafields win — an editor who has overridden the file means it.
    let episode = {
      audio: this.dataset.audio || '',
      seconds: parseInt(this.dataset.seconds, 10) || 0,
      chapters: null,
    };

    const indexUrl = this.dataset.index;
    const key = this.dataset.published;
    if (indexUrl && key && (!episode.audio || !episode.seconds)) {
      const index = await loadIndex(indexUrl);
      let found = index && index.episodes && index.episodes[key];
      // Newer than the index: read it from the show's feed instead. Only a
      // recent episode forces a fresh read when the cached feed lacks it, so
      // an old post that was never an episode does not refetch every visit.
      if (!found && index && index.shows) {
        const recent = Date.now() / 1000 - Number(key) < 30 * 24 * 60 * 60;
        const feed = await loadFeeds(index, recent ? key : null);
        found = feed[key] || null;
      }
      if (found) {
        episode = {
          audio: episode.audio || found.audio || '',
          seconds: episode.seconds || found.seconds || 0,
          chapters: found.chapters || null,
        };
        // Two shows publish into this blog, so fall back to the links of the
        // show this episode belongs to, not whichever one is listed first.
        const scoped = !!(index.shows && found.show && index.shows[found.show]);
        const show = scoped ? index.shows[found.show] : index.show;
        this.upgradeLinks(found, show, scoped);
        this.fillArtwork(found);
      }
    }

    if (!episode.audio) {
      this.setAttribute('data-state', 'unavailable');
      if (this.els.status) {
        this.els.status.hidden = false;
      }
      return;
    }

    this.episode = episode;
    this.audio.src = episode.audio;
    this.setAttribute('data-state', 'ready');

    if (episode.seconds) {
      this.duration = episode.seconds;
      if (this.els.duration) this.els.duration.textContent = clock(episode.seconds);
      if (this.els.range) this.els.range.max = String(episode.seconds);
    }

    this.renderChapters(episode.chapters);
    this.restorePosition();
  }

  /**
   * Older episodes predate the article images, so borrow the feed's cover art.
   * It is square, unlike the 16:9 article images, so the slot switches ratio.
   */
  fillArtwork(found) {
    const slot = this.els.art;
    if (!slot || slot.querySelector('img') || !found.artwork) return;
    const img = new Image();
    img.loading = 'lazy';
    img.alt = '';
    img.src = found.artwork;
    slot.append(img);
    slot.style.setProperty('--ap-art-ratio', '1');
    slot.hidden = false;
  }

  /** Point the platform buttons at this episode instead of the channel. */
  upgradeLinks(found, show, scoped) {
    this.querySelectorAll('[data-ap-link]').forEach((link) => {
      const platform = link.dataset.apLink;
      const url = platform === 'download' ? found.audio : found[platform];
      if (url) {
        link.href = url;
        link.dataset.apLinkScope = 'episode';
        if (platform === 'download') link.setAttribute('download', '');
      } else if (link.dataset.apLinkRequire === 'episode') {
        // The editor asked to hide this one unless it can deep-link.
        link.hidden = true;
      } else if (show && show[platform]) {
        link.href = show[platform];
        link.dataset.apLinkScope = 'show';
      } else if (scoped) {
        /* We know which show this episode belongs to and it has no link for
           this platform, so whatever the section put here belongs to a
           different show. Send nobody to the wrong podcast. */
        link.hidden = true;
      }
    });

    const transcript = this.querySelector('[data-ap-transcript]');
    if (transcript) {
      if (found.transcript) transcript.href = found.transcript;
      else transcript.hidden = true;
    }
  }

  /* ------------------------------------------------------------- controls */

  bindControls() {
    const { play, back, forward, speed, mute, range } = this.els;

    if (play) play.addEventListener('click', () => this.toggle());
    if (back) back.addEventListener('click', () => this.nudge(-this.skipBack));
    if (forward) forward.addEventListener('click', () => this.nudge(this.skipForward));

    if (speed) {
      speed.addEventListener('click', () => {
        this.speedIndex = (this.speedIndex + 1) % this.speeds.length;
        const rate = this.speeds[this.speedIndex];
        this.audio.playbackRate = rate;
        speed.textContent = `${rate}×`;
        speed.setAttribute('aria-label', `Playback speed ${rate} times. Click to change.`);
      });
    }

    if (mute) {
      mute.addEventListener('click', () => {
        this.audio.muted = !this.audio.muted;
        mute.setAttribute('aria-pressed', String(this.audio.muted));
        this.setAttribute('data-muted', String(this.audio.muted));
      });
    }

    if (range) {
      const scrub = () => {
        this.paint(parseFloat(range.value), this.duration);
      };
      range.addEventListener('input', () => {
        this.scrubbing = true;
        scrub();
      });
      range.addEventListener('change', () => {
        this.scrubbing = false;
        this.seek(parseFloat(range.value));
      });
      // A pointer release outside the track still ends the scrub.
      range.addEventListener('pointerup', () => {
        this.scrubbing = false;
      });
    }
  }

  bindAudio() {
    const a = this.audio;

    a.addEventListener('loadedmetadata', () => {
      if (Number.isFinite(a.duration) && a.duration > 0) {
        this.duration = a.duration;
        if (this.els.duration) this.els.duration.textContent = clock(a.duration);
        if (this.els.range) this.els.range.max = String(a.duration);
      }
    });

    a.addEventListener('timeupdate', () => {
      if (this.scrubbing) return;
      if (this.els.range) this.els.range.value = String(a.currentTime);
      this.paint(a.currentTime, this.duration);
      this.markChapter(a.currentTime);
      this.savePosition();
    });

    a.addEventListener('progress', () => this.paintBuffer());
    a.addEventListener('play', () => this.reflect(true));
    a.addEventListener('pause', () => this.reflect(false));
    a.addEventListener('waiting', () => this.setAttribute('data-state', 'loading'));
    a.addEventListener('playing', () => this.setAttribute('data-state', 'ready'));
    a.addEventListener('ended', () => {
      this.reflect(false);
      this.clearPosition();
    });
    a.addEventListener('error', () => {
      this.setAttribute('data-state', 'unavailable');
      if (this.els.status) this.els.status.hidden = false;
    });
  }

  /* -------------------------------------------------------------- playback */

  toggle() {
    if (this.audio.paused) {
      this.audio.play().catch(() => {
        this.setAttribute('data-state', 'unavailable');
      });
    } else {
      this.audio.pause();
    }
  }

  nudge(by) {
    this.seek(this.audio.currentTime + by);
  }

  seek(to) {
    const max = this.duration || this.audio.duration || 0;
    this.audio.currentTime = Math.min(Math.max(to, 0), max || to);
  }

  reflect(playing) {
    this.setAttribute('data-playing', String(playing));
    if (this.els.play) {
      this.els.play.setAttribute('aria-pressed', String(playing));
      const label = playing ? this.dataset.pauseLabel : this.dataset.playLabel;
      if (label) this.els.play.setAttribute('aria-label', label);
    }
    if (playing) this.announceToOs();
  }

  /* ------------------------------------------------------------- painting */

  paint(current, total) {
    if (this.els.current) this.els.current.textContent = clock(current);
    const pct = total > 0 ? (current / total) * 100 : 0;
    if (this.els.fill) this.els.fill.style.width = `${pct}%`;
  }

  paintBuffer() {
    if (!this.els.buffer) return;
    const total = this.duration || this.audio.duration || 0;
    const ranges = this.audio.buffered;
    if (!total || !ranges.length) return;
    const end = ranges.end(ranges.length - 1);
    this.els.buffer.style.width = `${Math.min((end / total) * 100, 100)}%`;
  }

  /* ------------------------------------------------------------- chapters */

  renderChapters(chapters) {
    const { chapters: wrap, chapterList: list } = this.els;
    if (!wrap || !list) return;
    if (!chapters || !chapters.length) {
      wrap.hidden = true;
      return;
    }

    list.textContent = '';
    chapters.forEach((chapter, i) => {
      const item = document.createElement('li');
      item.className = 'sm-ap__chapter';

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'sm-ap__chapter-button';
      button.dataset.apChapter = String(i);
      button.dataset.start = String(chapter.start);

      const time = document.createElement('span');
      time.className = 'sm-ap__chapter-time';
      time.textContent = clock(chapter.start);

      const title = document.createElement('span');
      title.className = 'sm-ap__chapter-title';
      title.textContent = chapter.title;

      button.append(time, title);
      button.addEventListener('click', () => {
        this.seek(chapter.start);
        if (this.audio.paused) this.toggle();
      });

      item.append(button);
      list.append(item);
    });

    wrap.hidden = false;
    this.chapterStarts = chapters.map((c) => c.start);
  }

  markChapter(time) {
    if (!this.chapterStarts || !this.els.chapterList) return;
    let active = -1;
    for (let i = 0; i < this.chapterStarts.length; i++) {
      if (time >= this.chapterStarts[i]) active = i;
    }
    if (active === this.activeChapter) return;
    this.activeChapter = active;
    this.els.chapterList.querySelectorAll('[data-ap-chapter]').forEach((el, i) => {
      el.toggleAttribute('data-active', i === active);
    });
  }

  /* ------------------------------------------- resume where they left off */

  get storageKey() {
    return `smap:${this.dataset.published || this.dataset.audio || ''}`;
  }

  savePosition() {
    if (!this.duration || this.audio.currentTime < 30) return;
    if (this._savedAt && Date.now() - this._savedAt < 5000) return;
    this._savedAt = Date.now();
    try {
      localStorage.setItem(this.storageKey, String(Math.floor(this.audio.currentTime)));
    } catch (e) {
      /* private browsing, blocked storage — the player still works */
    }
  }

  restorePosition() {
    try {
      const saved = parseInt(localStorage.getItem(this.storageKey), 10);
      // Ignore a position within a minute of the end; they finished it.
      if (saved > 30 && (!this.duration || saved < this.duration - 60)) {
        this.audio.currentTime = saved;
        if (this.els.range) this.els.range.value = String(saved);
        this.paint(saved, this.duration);
      }
    } catch (e) {
      /* see savePosition */
    }
  }

  clearPosition() {
    try {
      localStorage.removeItem(this.storageKey);
    } catch (e) {
      /* see savePosition */
    }
  }

  /* --------------------------------------------- OS media controls, if any */

  announceToOs() {
    if (!('mediaSession' in navigator) || this._osReady) return;
    this._osReady = true;

    const artwork = this.dataset.artwork;
    navigator.mediaSession.metadata = new MediaMetadata({
      title: this.dataset.title || document.title,
      artist: this.dataset.show || '',
      album: this.dataset.show || '',
      artwork: artwork ? [{ src: artwork, sizes: '512x512', type: 'image/jpeg' }] : [],
    });

    navigator.mediaSession.setActionHandler('play', () => this.toggle());
    navigator.mediaSession.setActionHandler('pause', () => this.toggle());
    navigator.mediaSession.setActionHandler('seekbackward', () => this.nudge(-this.skipBack));
    navigator.mediaSession.setActionHandler('seekforward', () => this.nudge(this.skipForward));
    navigator.mediaSession.setActionHandler('seekto', (details) => {
      if (details.seekTime != null) this.seek(details.seekTime);
    });
  }
}

if (!customElements.get('sm-audio-player')) {
  customElements.define('sm-audio-player', SmAudioPlayer);
}
